import * as Sentry from '@sentry/react-native';

export interface SessionEvent {
  eventType: 'login' | 'logout' | 'modal_shown' | 'modal_skipped' | '401_received' | 'session_extended';
  userId?: string;
  sessionId: string;
  timestamp: number;
  timeSinceLogin?: number;
  inactivityDuration?: number;
  cooldownActive?: boolean;
  source: 'session_manager' | 'base_query' | 'auth_modal' | 'user_action';
  metadata?: Record<string, any>;
}

/**
 * Session Analytics Service
 * 
 * Tracks session-related events to Sentry for debugging duplicate auth modal issues.
 * Provides insights into user session patterns and identifies problematic authentication flows.
 */
export class SessionAnalytics {
  private static sessionId: string | null = null;
  private static userId: string | null = null;

  /**
   * Initialize session analytics with user context
   */
  static initialize(userId?: string): void {
    this.sessionId = this.generateSessionId();
    this.userId = userId || null;
    
    // Set user context in Sentry for session tracking
    if (this.userId) {
      Sentry.setUser({ id: this.userId });
    }
    
    Sentry.setTag('session_id', this.sessionId);
    Sentry.setTag('session_analytics', 'enabled');
  }

  /**
   * Track a session-related event
   */
  static trackEvent(event: Omit<SessionEvent, 'sessionId' | 'timestamp'>): void {
    if (!this.sessionId) {
      console.warn('SessionAnalytics not initialized');
      return;
    }

    const fullEvent: SessionEvent = {
      ...event,
      sessionId: this.sessionId,
      timestamp: Date.now(),
    };

    // Send to Sentry as breadcrumb
    Sentry.addBreadcrumb({
      message: `Session ${fullEvent.eventType}`,
      category: 'session',
      level: this.getLogLevel(fullEvent.eventType),
      data: {
        sessionId: fullEvent.sessionId,
        userId: fullEvent.userId,
        timeSinceLogin: fullEvent.timeSinceLogin,
        inactivityDuration: fullEvent.inactivityDuration,
        cooldownActive: fullEvent.cooldownActive,
        source: fullEvent.source,
        metadata: fullEvent.metadata,
      },
    });

    // Send important events as custom Sentry events
    if (this.isImportantEvent(fullEvent.eventType)) {
      Sentry.captureMessage(`Session Event: ${fullEvent.eventType}`, {
        level: this.getLogLevel(fullEvent.eventType),
        tags: {
          session_event: fullEvent.eventType,
          source: fullEvent.source,
          user_id: fullEvent.userId || 'anonymous',
        },
        extra: {
          sessionData: fullEvent,
        },
      });
    }

    // Console logging for development
    if (__DEV__) {
      console.log(`🔍 Session Analytics: ${fullEvent.eventType}`, fullEvent);
    }
  }

  /**
   * Track login event
   */
  static trackLogin(source: 'initial' | 'reauth', userId?: string): void {
    if (userId) {
      this.userId = userId;
      Sentry.setUser({ id: userId });
    }

    this.trackEvent({
      eventType: 'login',
      userId: this.userId || undefined,
      source: source === 'initial' ? 'user_action' : 'auth_modal',
      metadata: { loginType: source },
    });
  }

  /**
   * Track logout event
   */
  static trackLogout(reason: 'user_action' | 'timeout' | '401'): void {
    this.trackEvent({
      eventType: 'logout',
      userId: this.userId || undefined,
      source: reason === 'user_action' ? 'user_action' : 'session_manager',
      metadata: { logoutReason: reason },
    });

    // Clear user context on logout
    Sentry.setUser(null);
    this.userId = null;
  }

  /**
   * Track when auth modal is shown
   */
  static trackModalShown(trigger: 'inactivity' | '401', timeSinceLogin?: number, inactivityDuration?: number): void {
    this.trackEvent({
      eventType: 'modal_shown',
      userId: this.userId || undefined,
      timeSinceLogin,
      inactivityDuration,
      source: 'session_manager',
      metadata: { 
        trigger,
        cooldownActive: timeSinceLogin ? timeSinceLogin <= 15000 : false, // 15 seconds cooldown
      },
    });
  }

  /**
   * Track when auth modal is skipped (due to cooldown, etc.)
   */
  static trackModalSkipped(reason: 'cooldown' | 'already_shown' | 'not_logged_in', timeSinceLogin?: number): void {
    this.trackEvent({
      eventType: 'modal_skipped',
      userId: this.userId || undefined,
      timeSinceLogin,
      cooldownActive: reason === 'cooldown',
      source: 'session_manager',
      metadata: { skipReason: reason },
    });
  }

  /**
   * Track 401 errors
   */
  static track401Received(endpoint: string, timeSinceLogin?: number): void {
    this.trackEvent({
      eventType: '401_received',
      userId: this.userId || undefined,
      timeSinceLogin,
      cooldownActive: timeSinceLogin ? timeSinceLogin <= 15000 : false,
      source: 'base_query',
      metadata: { endpoint },
    });
  }

  /**
   * Track session extension (if implemented)
   */
  static trackSessionExtension(): void {
    this.trackEvent({
      eventType: 'session_extended',
      userId: this.userId || undefined,
      source: 'auth_modal',
    });
  }

  /**
   * Generate a unique session ID
   */
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Determine log level based on event type
   */
  private static getLogLevel(eventType: SessionEvent['eventType']): 'fatal' | 'error' | 'warning' | 'info' | 'debug' {
    switch (eventType) {
      case 'modal_shown':
        return 'warning'; // Modal shown is a warning level event
      case '401_received':
        return 'error'; // 401 is an error
      case 'logout':
        return 'info';
      case 'login':
        return 'info';
      case 'modal_skipped':
        return 'debug';
      case 'session_extended':
        return 'info';
      default:
        return 'info';
    }
  }

  /**
   * Determine if event should be sent as a full Sentry event
   */
  private static isImportantEvent(eventType: SessionEvent['eventType']): boolean {
    return ['modal_shown', '401_received', 'login', 'logout'].includes(eventType);
  }

  /**
   * Get current session info for debugging
   */
  static getSessionInfo(): { sessionId: string | null; userId: string | null } {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
    };
  }
}
